/**
 * Unit IDs that provide a persistent positive aura to other friendly units
 * (stat buffs, ability grants, protection, combat enhancement).
 *
 * Excludes drone auras and player-wide effects (intel/cost/charge bonuses).
 * Source of truth for battlefield aura-provider VFX.
 */
export const FRIENDLY_AURA_PROVIDER_UNIT_IDS = Object.freeze([
  // Stat buffs to other friendlies
  1, // Thrallborn Howler
  16, // Hiveguard Sentinel — other friendlies +0/+1
  88, // Exolot Corporal
  248, // Ba
  339, // Master Tactician
  423, // Clan Matriarch
  // Ability grants
  89, // Battlefield Orator
  236, // Wepwawet
  // Protection / immunity
  290, // Alley Sentinel
  300, // Toxicologist
  // Combat enhancement
  336, // Hollow Trickster
  // Event / deploy triggered friendly support
  12, // Corrupted Engineer — grow random friendly after strategem request
  34, // Sankrat Drill Sergeant — deployed units grow +1/+0
  // Persistent friendly support (turn / deploy / player-wide)
  32, // Lorran Mindworm — general heals per unit deployed
  233, // Taweret — heal another friendly at turn start
  234, // Seker — grow friendly deploys +0/+2
  235, // Heqet — orders at turn end from zone deaths
  237, // Priest of Osiris — +1 max intelligence while in play
  392, // Troa Ossek — deploy random strategy unit at turn start
  393, // Essia Luhr — strategems cost 1 less per copy in play
  43, // Neurocore Overseer — deployed drones keep charges
]);

const FRIENDLY_AURA_PROVIDER_UNIT_ID_SET = new Set(FRIENDLY_AURA_PROVIDER_UNIT_IDS);

export function isUnitConfused(order) {
  return order?.confused === true || order?.isConfused === true;
}

export function isFriendlyAuraProviderUnit(order) {
  if (!order || order.id === undefined || order.id === null) {
    return false;
  }
  const id = Number(order.id);
  if (Number.isNaN(id)) {
    return false;
  }
  return FRIENDLY_AURA_PROVIDER_UNIT_ID_SET.has(id);
}

/**
 * Whether the battlefield aura cloud VFX should render for this unit.
 * Mirrors backend rules: confused aura sources stop producing auras (AuraSystem skips them).
 */
export function shouldShowFriendlyAuraCloudEffect(
  order,
  { zoneName, isHiddenDuringAnimation = false } = {}
) {
  if (!zoneName || isHiddenDuringAnimation) {
    return false;
  }
  if (!isFriendlyAuraProviderUnit(order)) {
    return false;
  }
  if (isUnitConfused(order)) {
    return false;
  }
  return true;
}
